import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, CloudUpload } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import Pagination from "../components/Paginate";
import DateRangePicker from '../components/DateRangePicker';
import axiosInstance from '../utils/axiosInstance';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import ToggleSwitch from '../components/ToggleSwitch';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import LoadingSpinner from '../components/LoadingSpinner';

const ServiceArea = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceAreas, setServiceAreas] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
  const [loading, setLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [uploading, setUploading] = useState(false);
  const defaultFilters = {
    search: "",
    page: 1,
    limit: 10,
    startDate: null,
    endDate: null
  };
  
  const [filters, setFilters] = useState(defaultFilters);

  const buildQueryString = (filters) => {
    const queryParams = new URLSearchParams();
    
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.startDate) queryParams.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) queryParams.append('endDate', filters.endDate.toISOString());
    if (filters.page ) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);
    
    return queryParams.toString();
  };
  const getSerialNumber = (index) => {
    return ((pagination.currentPage - 1) * pagination.itemsPerPage) + index + 1;
  };
  useEffect(() => {
    const restoreFilters = () => {
      let initialFilters = { ...defaultFilters };
  
      // Get values from URL if they exist
      const page = searchParams.get('page');
      const limit = searchParams.get('limit');
      const search = searchParams.get('search');
      const startDate = searchParams.get('startDate');
      const endDate = searchParams.get('endDate');
  
      // Only override defaults if URL params exist and are valid
      if (page) initialFilters.page = Math.max(1, parseInt(page) || 1);
      if (limit) initialFilters.limit = Math.max(1, parseInt(limit) || 10);
      if (search) initialFilters.search = search;
      if (startDate) initialFilters.startDate = new Date(startDate);
      if (endDate) initialFilters.endDate = new Date(endDate);
  
      setFilters(initialFilters);
      setLocalSearch(initialFilters.search || '');
      setIsInitialized(true);
    };
  
    restoreFilters();
  }, []);
  const fetchServiceAreas = async (queryString) => {
    setLoading(true);
    console.log(queryString,"fsddf")
    try {
 
      const response = await axiosInstance.get(`service-areas/list?${queryString}`);
      setServiceAreas(response.data.data.serviceAreas);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error("Error fetching service areas:", error);
      showErrorToast(error.response?.data?.message || 'Error fetching service areas');
    } finally {
      setLoading(false);
    }
  };

  // Initialize filters from URL


  // Sync filters to URL and fetch data
  useEffect(() => {
    if (!isInitialized) return;
  
    const updateUrlAndFetch = () => {
      const isIncompleteDateRange = 
        (filters.startDate && !filters.endDate) || 
        (!filters.startDate && filters.endDate);
  
      if (isIncompleteDateRange) return;
  
      // Ensure default values for page and limit
      const finalFilters = {
        ...filters,
        page: filters.page || 1,
        limit: filters.limit || 10
      };
  console.log(finalFilters)
      const queryString = buildQueryString(finalFilters);
      const currentQueryString = searchParams.toString();
  console.log(queryString)
      // Compare current URL params with new params, accounting for defaults
      const currentParams = new URLSearchParams(currentQueryString);
      const newParams = new URLSearchParams(queryString);
      
   
  
      const paramsChanged = currentParams.toString() !== newParams.toString();
  
      if (paramsChanged) {
        setSearchParams(queryString ? `?${queryString}` : {}, { replace: true });
      }
  console.log("first",queryString ? `?${queryString}` : {}, { replace: true })
      fetchServiceAreas(queryString);
    };
  
    updateUrlAndFetch();
  }, [filters, isInitialized]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setFilters(prev => ({ ...prev, search: value, page: 1 }));
      }, 500),
    []
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleDateChange = (dates) => {
    if (!dates.startDate && !dates.endDate) {
      setFilters(prev => ({ ...prev, startDate: null, endDate: null, page: 1 }));
    } else {
      setFilters(prev => ({ ...prev, ...dates, page: 1 }));
    }
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setLocalSearch("");
    debouncedSearch.cancel();
    setSearchParams({}, { replace: true });
  };

  const handleToggleBlock = (areaId, currentStatus) => {
    confirmAlert({
      title: 'Confirm Status Change',
      message: `Are you sure you want to ${currentStatus ? 'unblock' : 'block'} this service area?`,
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await axiosInstance.patch(`service-areas/toggle-block/${areaId}`, {
                isBlocked: !currentStatus
              });

              setServiceAreas(areas =>
                areas.map(area =>
                  area.id === areaId ? { ...area, isBlocked: !currentStatus } : area
                )
              );

              showSuccessToast(`Service area ${!currentStatus ? 'blocked' : 'unblocked'} successfully`);
            } catch (error) {
              showErrorToast(error.response?.data?.message || 'Failed to update block status');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await axiosInstance.post('service-areas/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSuccessToast('File uploaded successfully!');
      const queryString = buildQueryString(filters);
      fetchServiceAreas(queryString);
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error uploading file!');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Service Areas</h1>
        <div className="flex items-center gap-4">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
          <a
            href="/sample.xlsx"
            download
            className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Download Sample Excel
          </a>
          <div className="relative">
            <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">
              <CloudUpload className="h-4 w-4 text-gray-400" />
              {uploading ? 'Uploading...' : 'Upload File'}
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
                accept=".csv,.xlsx,.xls"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search service areas..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="w-full sm:w-[300px]">
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div><LoadingSpinner/></div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
              <th className="px-6 py-3 text-left">S.No</th>
                <th className="px-6 py-3 text-left">City</th>
                <th className="px-6 py-3 text-left">Pin Code</th>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Is Blocked</th>
                <th className="px-6 py-3 text-left">Created At</th>
                <th className="px-6 py-3 text-left">Updated At</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceAreas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                    No service areas found
                  </td>
                </tr>
              ) : (
                serviceAreas.map((area,index) => (
                  <tr key={area.id}>
                        <td className="px-6 py-4">{getSerialNumber(index)}</td>
                    <td className="px-6 py-4">{area.city}</td>
                    <td className="px-6 py-4">{area.pinCode}</td>
                    <td className="px-6 py-4">{area.type}</td>
                    <td className="px-6 py-4">
                      <ToggleSwitch
                        checked={area.isBlocked}
                        onChange={() => handleToggleBlock(area.id, area.isBlocked)}
                        enabledText="Blocked"
                        disabledText="Active"
                      />
                    </td>
                    <td className="px-6 py-4">{new Date(area.createdAt).toLocaleString()}</td>
                    <td className="px-6 py-4">{new Date(area.updatedAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && serviceAreas.length > 0 && (
        <Pagination
          currentPage={filters.page}
          totalItems={pagination.totalItems}
          pageSize={filters.limit}
          onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
          onPageSizeChange={(limit) => setFilters(prev => ({ ...prev, limit, page: 1 }))}
        />
      )}
    </div>
  );
};

export default ServiceArea;